/**
 * Watching the queue.
 *
 * Refreshing is not free. Every refresh with `refresh=true` makes the server
 * ask the GPU, and while the GPU is off that costs a couple of seconds on a
 * connect that never completes. So the poll runs fast only while something is
 * actually happening, and an idle page reads our own mirror without touching
 * the VM at all.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { api, ApiError, type Job } from '../api/client'

const ACTIVE_INTERVAL_MS = 3_000
const IDLE_INTERVAL_MS = 30_000

export function isActive(job: Job): boolean {
  return !job.is_terminal
}

/** Terminal, but we may still be fetching its results off the GPU. */
function stillSettling(job: Job): boolean {
  return job.is_terminal && job.state === 'completed' && !job.results_mirrored
}

export function useJobs() {
  const [jobs, setJobs] = useState<Job[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const inFlight = useRef(false)

  const load = useCallback(async (askTheGpu: boolean) => {
    if (inFlight.current) return
    inFlight.current = true
    try {
      const next = await api.listJobs(askTheGpu)
      setJobs(next)
      setError(null)
    } catch (cause) {
      // A 401 is already being handled globally; anything else is worth saying,
      // but never worth throwing away the list we already have.
      if (!(cause instanceof ApiError && cause.isUnauthenticated)) {
        setError(cause instanceof Error ? cause.message : 'Could not read the queue.')
      }
    } finally {
      inFlight.current = false
    }
  }, [])

  useEffect(() => {
    void load(true)
  }, [load])

  useEffect(() => {
    // Anything unfinished, or finished but not yet copied off the GPU, is worth
    // watching closely; otherwise tick along slowly and leave the VM alone.
    const busy = jobs?.some((job) => isActive(job) || stillSettling(job)) ?? false
    timer.current = window.setTimeout(
      () => void load(busy),
      busy ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS,
    )
    return () => window.clearTimeout(timer.current)
  }, [jobs, load])

  const refreshNow = useCallback(() => load(true), [load])

  return { jobs, error, refreshNow }
}
